import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import {
  ResponsiveContainer, ComposedChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, Legend,
} from 'recharts';
import { DashboardLayout } from '../components/layout';
import { adminApi } from '../api/examApi';
import { DASH, fmtPct } from '../lib/reportFormat';
import { useTheme } from '../context/ThemeContext';
import { InfoTooltip, HowToReadPanel } from '../components/reports/ReportTooltip';

const todayIso = () => new Date().toISOString().slice(0, 10);

// docs/reporting-metrics-spec.md §13.2 — the owner has not yet answered
// whether a student may ever see their own rank. The backend always
// computes and returns `rank` for this admin-only endpoint regardless
// (reporting_service.py's STUDENT_RANK_VISIBLE_TO_STUDENT gates a
// DIFFERENT, student-facing surface, not this one) — but per this
// session's instruction, nothing on THIS page renders it until he
// decides. Flip this single constant when he answers; nothing else
// about the page needs to change.
const RANK_VISIBLE = false;

// A small fixed palette for the per-subject score-trend lines, drawn from
// hues already used elsewhere in this app's reporting surfaces (primary
// teal, amber, rose family) — cycles if a student has more subjects than
// colours.
const SUBJECT_COLORS = ['#04405c', '#d97706', '#7c3aed', '#0891b2', '#c026d3', '#65a30d'];

const fmtDate = (iso) => new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
const fmtAxisDate = (ts) => new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

function Tile({ id, label, value, sub, tone, tooltip, ...rest }) {
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4" {...rest}>
      <p className="text-xs font-medium text-slate-500 dark:text-slate-400 inline-flex items-center gap-1">
        {label}
        {tooltip && <InfoTooltip id={id} text={tooltip} />}
      </p>
      <p className={`mt-1 text-2xl font-bold ${tone || 'text-slate-900 dark:text-white'}`}>{value}</p>
      {sub && <p className="mt-0.5 text-xs text-slate-400 dark:text-slate-500">{sub}</p>}
    </div>
  );
}

function ScoreTrendTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const point = payload[0].payload;
  return (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs shadow-lg dark:border-slate-700 dark:bg-slate-800">
      <p className="font-semibold text-slate-900 dark:text-white">{point.title}</p>
      <p className="text-slate-500 dark:text-slate-400">{point.subject} &middot; {fmtDate(point.date)}</p>
      <p className="mt-1 font-medium text-primary dark:text-accent-cool">{point.pct.toFixed(1)}%</p>
    </div>
  );
}

// §13.3 #1 — score trend split by subject. One <Line> per subject, each
// carrying its own `data` array (same per-series-data pattern
// StudentProgressDashboard.js already uses), so subjects with gaps don't
// interpolate across each other.
function ScoreTrendChart({ scoreTrend, gridColor, tickColor }) {
  const bySubject = useMemo(() => {
    const map = new Map();
    scoreTrend.forEach((p) => {
      const key = p.subject || 'Unspecified';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push({
        x: new Date(p.attempted_at).getTime(),
        date: p.attempted_at,
        subject: p.subject,
        title: p.title,
        pct: p.pct,
      });
    });
    for (const points of map.values()) points.sort((a, b) => a.x - b.x);
    return Array.from(map.entries());
  }, [scoreTrend]);

  if (scoreTrend.length === 0) {
    return (
      <p className="py-12 text-center text-sm text-slate-500 dark:text-slate-400" data-testid="score-trend-empty">
        No scored exams in this period.
      </p>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={280}>
      <ComposedChart margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
        <CartesianGrid stroke={gridColor} strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="x"
          type="number"
          domain={['dataMin', 'dataMax']}
          tickFormatter={fmtAxisDate}
          stroke={tickColor}
          tick={{ fontSize: 11, fill: tickColor }}
        />
        <YAxis domain={[0, 100]} unit="%" stroke={tickColor} tick={{ fontSize: 11, fill: tickColor }} width={44} />
        <Tooltip content={<ScoreTrendTooltip />} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {bySubject.map(([subject, points], i) => (
          <Line
            key={subject}
            name={subject}
            data={points}
            dataKey="pct"
            stroke={SUBJECT_COLORS[i % SUBJECT_COLORS.length]}
            strokeWidth={2}
            dot={{ r: 4, fill: SUBJECT_COLORS[i % SUBJECT_COLORS.length] }}
            connectNulls
            isAnimationActive={false}
          />
        ))}
      </ComposedChart>
    </ResponsiveContainer>
  );
}

// §13.3 #2 — percentile band per (exam, group), with cohort size always
// shown alongside (§1.4: a percentile with no cohort size next to it is
// noise). §1.4/§13.4: suppressed as "—", never 0, for cohort_size < 2;
// distinguished from "no group at all" via `in_any_group`, since an empty
// list means two different things and must not be conflated.
function PercentileBand({ inAnyGroup, rows, examTitleById }) {
  if (!inAnyGroup) {
    return (
      <div data-testid="percentile-band-no-group">
        <span className="text-slate-400">{DASH}</span>
        <p className="text-xs text-slate-400 mt-0.5">not in a group</p>
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div data-testid="percentile-band-empty">
        <span className="text-slate-400">{DASH}</span>
        <p className="text-xs text-slate-400 mt-0.5">no exams in this period</p>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="percentile-band-table">
        <thead>
          <tr className="text-left text-xs text-slate-500 dark:text-slate-400">
            <th className="pr-3 py-1 font-medium">Exam</th>
            <th className="pr-3 py-1 font-medium">Group</th>
            <th className="pr-3 py-1 font-medium">Score</th>
            <th className="py-1 font-medium">Percentile</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
          {rows.map((row) => (
            <tr key={`${row.exam_id}-${row.group_id}`} data-testid={`percentile-row-${row.exam_id}-${row.group_id}`}>
              <td className="pr-3 py-1.5 text-slate-800 dark:text-slate-200">{examTitleById.get(row.exam_id) || row.exam_id}</td>
              <td className="pr-3 py-1.5 text-slate-500 dark:text-slate-400">{row.group_name}</td>
              <td className="pr-3 py-1.5 text-slate-800 dark:text-slate-200">{fmtPct(row.pct_score)}</td>
              <td className="py-1.5">
                <span className="text-slate-800 dark:text-slate-200 font-medium" data-testid={`percentile-value-${row.exam_id}-${row.group_id}`}>
                  {fmtPct(row.percentile)}
                </span>
                <span className="ml-1.5 text-xs text-slate-400">(cohort of {row.cohort_size})</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// §13.3 #3 — submissions received, on_time / late / no_upload (§1.1's
// three-state punctuality — never `submissions.is_late`). Same visual
// language as the faculty drill-down's bar, without the drill-down's
// per-group two-plus-no-upload emphasis, which doesn't apply to a single
// student.
function SubmissionsBar({ on_time, late, no_upload }) {
  const total = on_time + late + no_upload;
  if (total === 0) {
    return (
      <div data-testid="submissions-empty">
        <span className="text-slate-400">{DASH}</span>
        <p className="text-xs text-slate-400 mt-0.5">no exams assigned in this period</p>
      </div>
    );
  }
  const pct = (n) => (100 * n) / total;
  return (
    <div className="max-w-sm" data-testid="submissions-bar">
      <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
        {on_time > 0 && <div style={{ width: `${pct(on_time)}%` }} className="bg-emerald-500" title={`${on_time} on time`} />}
        {late > 0 && <div style={{ width: `${pct(late)}%` }} className="bg-amber-500" title={`${late} late`} />}
        {no_upload > 0 && <div style={{ width: `${pct(no_upload)}%` }} className="bg-slate-300 dark:bg-slate-500" title={`${no_upload} none`} />}
      </div>
      <div className="mt-1 flex flex-wrap gap-x-3 text-[11px] text-slate-500 dark:text-slate-400">
        <span><span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 mr-1" />{on_time} on-time</span>
        <span><span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500 mr-1" />{late} late</span>
        <span><span className="inline-block h-1.5 w-1.5 rounded-full bg-slate-400 mr-1" />{no_upload} none</span>
      </div>
    </div>
  );
}

export default function AdminStudentReportPage() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dateFrom, setDateFrom] = useState(searchParams.get('date_from') || '');
  const [dateTo, setDateTo] = useState(searchParams.get('date_to') || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const gridColor = isDark ? '#334155' : '#e2e8f0';
  const tickColor = isDark ? '#94a3b8' : '#64748b';

  const load = useCallback((params) => {
    setLoading(true);
    adminApi
      .getStudentReport(studentId, params)
      .then(({ data: body }) => {
        setData(body);
        setDateFrom(body.date_from);
        setDateTo(body.date_to);
        setSearchParams({ date_from: body.date_from, date_to: body.date_to }, { replace: true });
      })
      .catch(() => toast.error('Failed to load the student report'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  useEffect(() => {
    const initial = {};
    const qFrom = searchParams.get('date_from');
    const qTo = searchParams.get('date_to');
    if (qFrom && qTo) {
      initial.date_from = qFrom;
      initial.date_to = qTo;
    }
    load(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  const applyRange = () => {
    if (!dateFrom || !dateTo) return;
    load({ date_from: dateFrom, date_to: dateTo });
  };

  const examTitleById = useMemo(() => {
    const map = new Map();
    (data?.score_trend || []).forEach((p) => map.set(p.exam_id, p.title));
    return map;
  }, [data]);

  const columnLabel = useCallback((id) => {
    const col = (data?.columns || []).find((c) => c.id === id);
    return col ? col.label : id;
  }, [data]);

  // §13.2 — nothing on this page surfaces rank yet (see RANK_VISIBLE above),
  // so its column/definition is dropped everywhere columns are rendered
  // generically, including the "How to read this page" panel — not just
  // skipped where a value would go.
  const visibleColumns = useMemo(() => (data?.columns || []).filter((c) => c.id !== 'rank'), [data]);

  // §12.6-pattern emphasis, colour/weight only — see the render comment below.
  const belowFifty = !!data && data.rolling_average.n > 0 && data.rolling_average.avg_pct < 50;
  const attendanceBelowThreshold = !!data && data.attendance_rate.n > 0
    && data.attendance_rate.present_pct < data.attendance_flag_threshold_pct;

  return (
    <DashboardLayout title="Student Report">
      <div className="animate-fade-in space-y-6">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-slate-600 hover:text-primary dark:text-slate-400 dark:hover:text-accent-cool text-sm"
          data-testid="back-to-previous"
        >
          <ArrowLeft size={16} /> Back
        </button>

        <div className="flex items-start gap-3">
          <div className="mt-0.5 rounded-lg bg-primary/10 dark:bg-accent-cool/10 p-2 text-primary dark:text-accent-cool">
            <BarChart3 size={20} />
          </div>
          <div>
            <h2 className="text-2xl font-heading font-bold text-slate-900 dark:text-white" data-testid="student-report-name">
              {loading || !data ? 'Loading…' : data.student_name}
            </h2>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Trend, cohort standing and submission behaviour for the selected date range. Read-only.
            </p>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">From</label>
              <input
                type="date"
                value={dateFrom}
                max={dateTo || todayIso()}
                onChange={(e) => setDateFrom(e.target.value)}
                className="rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-primary dark:focus:ring-accent-cool"
                data-testid="student-report-date-from"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">To</label>
              <input
                type="date"
                value={dateTo}
                min={dateFrom}
                onChange={(e) => setDateTo(e.target.value)}
                className="rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-primary dark:focus:ring-accent-cool"
                data-testid="student-report-date-to"
              />
            </div>
            <button
              type="button"
              onClick={applyRange}
              disabled={!dateFrom || !dateTo}
              className="px-4 py-2 rounded-md bg-primary dark:bg-accent-cool text-white text-sm font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
              data-testid="student-report-apply-range"
            >
              Apply
            </button>
          </div>
        </div>

        {loading || !data ? (
          <div className="animate-pulse space-y-4">
            <div className="h-28 rounded-lg bg-slate-200 dark:bg-slate-700" />
            <div className="h-64 rounded-lg bg-slate-200 dark:bg-slate-700" />
          </div>
        ) : (
          <>
            <HowToReadPanel definitions={data.metric_definitions} columns={visibleColumns} />

            {/* Summary tiles — §13.3 #4/#5/#6/#7, plus the §13.1 pending-count
                figure (not one of the eight §13.3 rows, so it carries no
                metric_definitions entry; it is rendered as a plain fact per
                §13.1's own disposition, no colour rule attached).

                §12.6-pattern emphasis (colour and weight only, no badges, no
                generated advice): attendance rate below the institute's
                attendance_flag_threshold_pct, and this student's own average
                below 50% — the same "below 50%" treatment §12.3's class
                average gets, applied to the student-level analogue of that
                figure. */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
              <Tile
                id="rolling_average"
                label={columnLabel('rolling_average')}
                tooltip={data.metric_definitions.rolling_average}
                value={data.rolling_average.n === 0 ? DASH : fmtPct(data.rolling_average.avg_pct)}
                sub={data.rolling_average.n === 0
                  ? 'no scored exams in this period'
                  : `${fmtPct(data.rolling_average.min_pct, 0)}–${fmtPct(data.rolling_average.max_pct, 0)} · n=${data.rolling_average.n}`}
                tone={belowFifty ? 'text-rose-600 dark:text-rose-400' : undefined}
                data-testid="tile-rolling-average"
              />
              <Tile
                id="attendance_rate"
                label={columnLabel('attendance_rate')}
                tooltip={data.metric_definitions.attendance_rate}
                value={data.attendance_rate.n === 0 ? DASH : fmtPct(data.attendance_rate.present_pct)}
                sub={data.attendance_rate.n === 0 ? 'no attendance recorded' : `n=${data.attendance_rate.n} entries`}
                tone={attendanceBelowThreshold ? 'text-rose-600 dark:text-rose-400' : undefined}
                data-testid="tile-attendance-rate"
              />
              <Tile
                id="days_since_last_attempt"
                label={columnLabel('days_since_last_attempt')}
                tooltip={data.metric_definitions.days_since_last_attempt}
                value={data.days_since_last_attempt === null ? DASH : `${data.days_since_last_attempt}d`}
                sub={data.days_since_last_attempt === null ? 'no attempts on record' : undefined}
              />
              <Tile
                id="assigned_vs_attempted"
                label={columnLabel('assigned_vs_attempted')}
                tooltip={data.metric_definitions.assigned_vs_attempted}
                value={data.assigned_vs_attempted.assigned === 0 ? DASH : `${data.assigned_vs_attempted.attempted} of ${data.assigned_vs_attempted.assigned}`}
                sub={data.assigned_vs_attempted.assigned === 0
                  ? 'no exams assigned in this period'
                  : `${data.assigned_vs_attempted.missed} missed`}
              />
              <Tile label="Results pending evaluation" value={data.pending_count} sub="as of right now" />
            </div>

            {/* §13.3 #1 */}
            <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
              <h3 className="mb-3 inline-flex items-center gap-1 text-sm font-semibold text-slate-900 dark:text-white">
                {columnLabel('score_trend')}
                <InfoTooltip id="score_trend" text={data.metric_definitions.score_trend} />
              </h3>
              <ScoreTrendChart scoreTrend={data.score_trend} gridColor={gridColor} tickColor={tickColor} />
            </div>

            {/* §13.3 #2 */}
            <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
              <h3 className="mb-3 inline-flex items-center gap-1 text-sm font-semibold text-slate-900 dark:text-white">
                {columnLabel('percentile_band')}
                <InfoTooltip id="percentile_band" text={data.metric_definitions.percentile_band} />
              </h3>
              <PercentileBand inAnyGroup={data.in_any_group} rows={data.percentile_band} examTitleById={examTitleById} />
              {RANK_VISIBLE && null /* §13.2 — rank rendering lands here once the owner decides */}
            </div>

            {/* §13.3 #3 */}
            <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
              <h3 className="mb-3 inline-flex items-center gap-1 text-sm font-semibold text-slate-900 dark:text-white">
                {columnLabel('submission_behavior')}
                <InfoTooltip id="submission_behavior" text={data.metric_definitions.submission_behavior} />
              </h3>
              <SubmissionsBar {...data.submission_behavior} />
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
